/**
 * Edit-report panel for reports.
 */
import { useQuery } from '@tanstack/react-query';
import { getSession } from '@/services/inperson/sessions.service';
import { REPORT_IN_PERSON_QUERY_KEYS as QUERY_KEYS } from '@/components/reports/report-configs';
import { InformationMeetingForm } from './report-form-builder/InformationMeetingForm';
import { TutoringReportForm } from './report-form-builder/TutoringReportForm';
import { Spinner } from '@/components/ui/spinner';
import { isInformationMeetingReport } from './report-form-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';

/** Props for {@link EditReportView}. */
interface EditReportViewProps {
  sessionId: string;
  reportId: string;
  sessionType?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Routes edit to tutoring or information-meeting form. */
export function EditReportView({
  sessionId,
  reportId,
  sessionType,
  onClose,
  onSuccess,
}: Readonly<EditReportViewProps>) {
  const {
    data: session,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: QUERY_KEYS.session(sessionId),
    queryFn: () => getSession(sessionId),
    enabled: !!sessionId && !sessionType,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'sessionNotFound',
    onClose,
    entityId: sessionId,
  });

  const resolvedType = sessionType ?? session?.type;

  if (!sessionType && isLoading) {
    return (
      <div className="form-panel-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  const formProps = { sessionId, reportId, onClose, onSuccess };

  if (isInformationMeetingReport(resolvedType)) {
    return <InformationMeetingForm {...formProps} />;
  }

  return <TutoringReportForm {...formProps} />;
}
